import { apiFetch } from "@/lib/api";

export type PreEncapDraftType = "FI-3C" | "FI-5A Chinese" | "FI-5A USA";
export type PreEncapDraftKind = "device" | "clamp";

export interface PreEncapDevice {
  serialFciId: number;
  deviceLabelSerial: string | null;
  clampSerial: string | null;
  finishedSku: string | null;
  serialFciNote: string | null;
  batchMfrRemark: string | null;
  pcbaSerial: string | null;
  hasValidPcbaCalibration: boolean;
  skuCoreFeature: string | null;
  stationId: number | null;
  stationName: string | null;
  shipmentId: number | null;
  locationId: number | null;
  buildOrderId: number | null;
  buildOrderNumber: string | null;
  buildOrderSku: string | null;
  hasOpenQualityIncident: boolean;
  openQualityIncidentId: number | null;
  openPottingReworkId: number | null;
  matchKind: "device_label" | "clamp" | null;
  routeDestination: "potting" | "conformal_coat" | null;
  routeStationId: number | null;
  routeStationName: string | null;
  routeTitle: string | null;
  packingInstruction: string | null;
  scanBlockedReason: string | null;
  draftAddRejection: string | null;
  autoAdvancedToConformalCoat?: boolean;
}

export interface PreEncapDraftRow {
  id: number;
  type: PreEncapDraftType;
  serial: string | null;
  skuCoreFeature: string | null;
  finishedSku: string | null;
  cartonNumber: number | null;
  cartonAssignedBy: string | null;
  laborSku: string | null;
  productLabel: string | null;
  sendFlow?: PreEncapPottingSendFlow;
}

export type PreEncapPottingSendFlow = "first_pass" | "internal_first_pass" | "rework" | "internal_rework";

export interface PreEncapPottingCartonSummary {
  flow: PreEncapPottingSendFlow;
  cartonNumber: number;
  laborSku: string;
  productLabel: string;
  sealed: boolean;
  sealedAt: string | null;
  labelPrintedAt?: string | null;
  rowCount: number;
}

export interface PreEncapAddDraftResult {
  ok: boolean;
  id: number;
  kind: PreEncapDraftKind;
  serial: string;
  laborRecorded?: boolean;
  cartonNumber?: number;
  laborSku?: string;
  productLabel?: string;
  createdNewCarton?: boolean;
}

export interface PreEncapAddUsaShellsResult {
  ok: boolean;
  count: number;
  buildOrderId: number;
  laborRecorded?: boolean;
  cartonNumber?: number;
  laborSku?: string;
  productLabel?: string;
  createdNewCarton?: boolean;
}

export interface PreEncapOpenWorkOrderOption {
  id: number;
  number: string;
  mvNumber: string | null;
  sku: string | null;
  label: string;
  quantityOrdered: number | null;
  quantityAssigned: number;
  quantityRemaining: number | null;
  description: string;
}

export const PRE_ENCAP_DEVICE_QUERY_KEY = ["manufacturing", "pre-encapsulation", "device"] as const;
export const PRE_ENCAP_DRAFT_QUERY_KEY = ["manufacturing", "pre-encapsulation", "draft"] as const;
export const PRE_ENCAP_CARTONS_QUERY_KEY = ["manufacturing", "pre-encapsulation", "cartons"] as const;
export const PRE_ENCAP_OPEN_WORK_ORDERS_QUERY_KEY = ["manufacturing", "pre-encapsulation", "work-orders"] as const;

export function fetchPreEncapOpenWorkOrders(search: string): Promise<{
  workOrders: PreEncapOpenWorkOrderOption[];
  allowedFinishedSkus: string[];
}> {
  return apiFetch<{
    workOrders: PreEncapOpenWorkOrderOption[];
    allowedFinishedSkus: string[];
  }>(
    "/api/manufacturing/pre-encapsulation/work-orders",
    {
      query: search.trim() === "" ? {} : { search: search.trim() },
    },
  );
}

export function fetchPreEncapDevice(serial: string): Promise<{ device: PreEncapDevice }> {
  return apiFetch<{ device: PreEncapDevice }>("/api/manufacturing/pre-encapsulation/device", {
    query: { serial },
  });
}

export interface PreEncapScanDraftResult {
  id: number;
  kind: PreEncapDraftKind | "rework";
  serial: string;
  laborRecorded: boolean;
  cartonNumber: number;
  laborSku: string;
  productLabel: string;
  createdNewCarton: boolean;
  flow?: PreEncapPottingSendFlow;
  reworkId?: number;
}

export interface PreEncapScanResult {
  ok: boolean;
  device: PreEncapDevice;
  stationAdvanced: boolean;
  laborRecorded: boolean;
  draftAdded?: boolean;
  draft?: PreEncapScanDraftResult;
  draftAddError?: string | null;
}

/** Register a floor scan; optionally add to the potting draft in the same request. */
export function scanPreEncapDevice(
  serial: string,
  options?: { addToDraft?: boolean; pottingPath?: "scs" | "internal" },
): Promise<PreEncapScanResult> {
  return apiFetch<PreEncapScanResult>("/api/manufacturing/pre-encapsulation/scan", {
    method: "POST",
    body: { serial, addToDraft: options?.addToDraft === true, pottingPath: options?.pottingPath },
  });
}

export function fetchPreEncapDraft(): Promise<{ rows: PreEncapDraftRow[] }> {
  return apiFetch<{ rows: PreEncapDraftRow[] }>("/api/manufacturing/pre-encapsulation/draft", {
    cache: "no-store",
  });
}

export function addPreEncapDraft(input: {
  kind: PreEncapDraftKind;
  serial: string;
  buildOrderId?: number | null;
}): Promise<PreEncapAddDraftResult> {
  return apiFetch<PreEncapAddDraftResult>("/api/manufacturing/pre-encapsulation/draft", {
    method: "POST",
    body: input,
  });
}

export function addPreEncapUsaShells(input: {
  count: number;
  buildOrderId: number;
  pottingPath?: "scs" | "internal";
}): Promise<PreEncapAddUsaShellsResult> {
  return apiFetch<PreEncapAddUsaShellsResult>("/api/manufacturing/pre-encapsulation/usa-shells", {
    method: "POST",
    body: input,
  });
}

export function removePreEncapDraft(id: number): Promise<{ ok: boolean; id: number }> {
  return apiFetch<{ ok: boolean; id: number }>(`/api/manufacturing/pre-encapsulation/draft/${id}`, {
    method: "DELETE",
  });
}

export interface PreEncapRouteResult {
  ok: boolean;
  serial: string;
  stationId: number;
  stationName: string | null;
  laborRecorded?: boolean;
}

export function routePreEncapDevice(serial: string): Promise<PreEncapRouteResult> {
  return apiFetch<PreEncapRouteResult>("/api/manufacturing/pre-encapsulation/route", {
    method: "POST",
    body: { serial },
  });
}

export function fetchPreEncapCartons(): Promise<{ cartons: PreEncapPottingCartonSummary[] }> {
  return apiFetch<{ cartons: PreEncapPottingCartonSummary[] }>("/api/manufacturing/pre-encapsulation/cartons", {
    cache: "no-store",
  });
}

export function sealPreEncapCarton(
  cartonNumber: number,
  flow: PreEncapPottingSendFlow = "first_pass",
): Promise<{ ok: boolean; cartonNumber: number; sealed: boolean }> {
  return apiFetch(`/api/manufacturing/pre-encapsulation/cartons/${cartonNumber}/seal`, {
    method: "POST",
    query: { flow },
    body: {},
  });
}

export function printPreEncapCarton(
  cartonNumber: number,
  printer: string,
  flow: PreEncapPottingSendFlow = "first_pass",
): Promise<{ printed: boolean; lineCount: number }> {
  return apiFetch(`/api/manufacturing/pre-encapsulation/cartons/${cartonNumber}/print`, {
    method: "POST",
    query: { flow },
    body: { printer },
  });
}

export { preEncapOpenQualityIncidentBlockMessage } from "@/lib/qc/qualityIncidentGuards";
